/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Turning what an agent typed or pasted into something dialable.
 *
 * Its own module rather than living in SoftphoneDock so it can be tested
 * without dragging the MUI and Twilio trees into Jest, and so the Call Console
 * can share it if it grows the same field.
 */

/**
 * Keep only what can be dialled, and only one leading +.
 *
 * Agents paste numbers out of emails and spreadsheets, so spaces, brackets and
 * dashes arrive constantly. Stripping them here rather than rejecting the
 * input means a pasted "+61 (2) 9876-5432" just works.
 */
export function sanitizeDialInput(raw) {
  const cleaned = (raw || "").replace(/[^\d+*#]/g, "");
  const plus = cleaned.startsWith("+");
  return (plus ? "+" : "") + cleaned.replace(/\+/g, "");
}

/**
 * Enough of a number to be worth trying.
 *
 * Deliberately not a dialling-plan validator: extensions, short codes and
 * international formats all differ, and the carrier is the authority. This
 * only catches a stray tap on the pad.
 */
export function isDialable(value) {
  return (value || "").replace(/\D/g, "").length >= 3;
}

/**
 * The pad's "+" key: an international number starts with "+" and the country
 * code. A "+" only means anything at the front, so the key puts it there
 * whatever has been keyed already, and pressing it twice changes nothing.
 */
export function withCountryPrefix(value) {
  const cleaned = sanitizeDialInput(value);
  return cleaned.startsWith("+") ? cleaned : `+${cleaned}`;
}
